import React, { useEffect, useState, useRef, useMemo } from 'react';
import swal from 'sweetalert2';

import { getStrokeExtraShot, getPlayerScore, getAdjustedHandicap, getPercentage } from '../tournamentHelper';
import { deleteScorecard, postScoreCard, sendDevilsDoubles666ScorecardMail, getTournamentHoleSelection, saveTournamentHoleSelection } from '../../api/tournamentAPI';

// ============================================================================
// DEVIL'S DOUBLES (6-6-6) - one-day format made of three 6-hole segments,
// each with its own scoring rule, UI style, and handicap calculation:
//
//   Holes 1-6   "Devil's Domain"      - Scramble UI. One combined team score
//                                       per hole. 40% of COMBINED handicap.
//   Holes 7-12  "Devil's Destruction" - Foursome UI. One combined team score
//                                       per hole. 50% of COMBINED handicap.
//                                       Drive is manually picked on EVERY
//                                       hole (same UI pattern as Domain's
//                                       Scramble drive selector) - there is
//                                       no auto-alternation. 3 holes in this
//                                       segment can be nominated by the team
//                                       for DOUBLE points, persisted via the
//                                       same getTournamentHoleSelection /
//                                       saveTournamentHoleSelection APIs used
//                                       by TournamentHolePlayerCheck.jsx.
//   Holes 13-18 "Devil's Finale"      - Worstball UI, but INVERTED: the
//                                       WORST nett score counts (not best).
//                                       FULL individual handicap per player,
//                                       each capped. Plus bonus points when
//                                       both players are at nett par or
//                                       better.
//
// Scoring throughout is Stableford, using ONE shared point table (pointData)
// - unlike ScrambleFoursomes/WorstballGreensome, which split pointData by
// PointName per format (see buildFormatPointData in ScrambleFoursomes.jsx).
// That's a deliberate difference: this format uses a single Stableford
// table for all three segments, so there is nothing to split here. If a
// future revision needs segment-specific point tables, the same
// buildFormatPointData pattern from ScrambleFoursomes.jsx is the template
// to reuse.
// ============================================================================

const DOMAIN_HOLES = Array.from({ length: 6 }, (_, i) => i);        // 0-5   (Holes 1-6)
const DESTRUCTION_HOLES = Array.from({ length: 6 }, (_, i) => i + 6);   // 6-11  (Holes 7-12)
const FINALE_HOLES = Array.from({ length: 6 }, (_, i) => i + 12);  // 12-17 (Holes 13-18)

const DESTRUCTION_START = 6;
const FINALE_START = 12;

const getSegmentForHole = (index) => {
    if (index < DESTRUCTION_START) return 'domain';
    if (index < FINALE_START) return 'destruction';
    return 'finale';
};

const MAX_PREFERRED_HOLES = 3;

// Preferred-holes state is keyed by hole NUMBER (7-12), one boolean per hole
// - team-level, unlike TournamentHolePlayerCheck's per-player grid, since
// Destruction has a single combined team score, not per-player scores.
const buildEmptyPreferredHoleChecks = () => {
    const state = {};
    DESTRUCTION_HOLES.forEach((index) => { state[index + 1] = false; }); // index+1 = hole number
    return state;
};

//#region Handicap helpers
// const getRoundFormatConfig = (roundFormatList, formatName, fallbackDraw) => {
//     const match = (roundFormatList || []).find(
//         (rf) => (rf.TournamentRoundFormat || '').toLowerCase() === formatName.toLowerCase()
//     );
//     if (match) return match;

//     if (fallbackDraw) {
//         return {
//             TournamentRoundFormat: formatName,
//             Lower: fallbackDraw.Lower,
//             LowerMiddle: 0,
//             Middle: 0,
//             Higher: fallbackDraw.Higher,
//             MinDrive: fallbackDraw.MinDrive,
//             IsMin: !!fallbackDraw?.MinDrive ? 1 : 0,
//         };
//     }
//     return null;
// };
const buildRoundFormatMap = (roundFormatList = []) => {
    const formatMap = new Map();
    for (const format of roundFormatList) {
        const name = format?.TournamentRoundFormat;
        if (name) {
            formatMap.set(
                name.toLowerCase(),
                format
            );
        }
    }

    return formatMap;
};

const getRoundFormatConfig = (formatMap, formatName, fallbackDraw) => {
    const match = formatMap.get(
        formatName.toLowerCase()
    );

    if (match) {
        return match;
    }

    if (!fallbackDraw) {
        return null;
    }

    return {
        TournamentRoundFormat: formatName,
        Lower: fallbackDraw.Lower,
        LowerMiddle: fallbackDraw.LowerMiddle,
        Middle: fallbackDraw.Middle,
        Higher: fallbackDraw.Higher,
        MinDrive: fallbackDraw.MinDrive,
        IsMin: fallbackDraw.MinDrive ? 1 : 0,
    };
};

// Devil's Finale: FULL individual handicap per player, capped at the format
const getCappedIndividualHandicap = (higher, lower, handicap, gender) => {
    if (handicap == null) return 0;
    const percentage = gender === 'Female' ? lower : higher;
    return Math.round(getPercentage(handicap, percentage));
};
//#endregion

//#region Point lookup (single shared Stableford table for all 3 segments)
const createPointFor = (pointData = []) => {
    if (!pointData.length) {
        return () => 0;
    }

    const pointsMap = new Map();
    let maxScore = -Infinity;
    let minScore = Infinity;

    for (const item of pointData) {
        const score = Number(item.Score);
        pointsMap.set(score, item.Point);
        if (score > maxScore) maxScore = score;
        if (score < minScore) minScore = score;
    }

    return (adjScore) => {
        const clamped = Math.max(minScore, Math.min(maxScore, adjScore));
        return pointsMap.get(clamped) ?? 0;
    };
};
//#endregion

//#region Shared per-player-on-a-hole calculation
const computeHolePoints = (playerInput, handicap, item, pointFor) => {
    const { Stroke, Par } = item;
    const score = getPlayerScore(playerInput, handicap, Stroke, Par);
    let point = 0;
    if (score.grossScore != 0) {
        const adj = score.IsScratch ? score.netScore : (score.netScore - Par);
        point = pointFor(adj);
    }
    return { ...score, point };
};
//#endregion

//#region Devil's Domain / Devil's Destruction - one combined team score per hole
// Shared shape between the two "single combined score" segments. `isDoubled`
// only ever applies to Destruction holes (Domain never doubles).
const buildCombinedScoreEntry = (item, hole, handicap, pointFor, isDoubled, selectedPlayer) => {
    const result = computeHolePoints(hole.player1Score || 0, handicap, item, pointFor);
    let point = result.point;
    const wasDoubled = !!isDoubled && result.grossScore != 0;
    if (wasDoubled) point *= 2;

    return {
        HoleNo: item.HoleNoValue,
        player1GrossScore: result.grossScore,
        player1NetScore: result.netScore,
        player1Point: point,
        maxTotal: point,
        player1Scratch: result.IsScratch,
        doubled: wasDoubled,
        driveUserID: hole.driveUserID || selectedPlayer.PlayerID,
    };
};

//#endregion

//#region Devil's Finale - worst nett counts + bonus points
// Nett-vs-par quality tier: 0 = nett par, 1 = nett birdie, 2 = nett eagle or
// better. null = worse than nett par (bogey or worse), which disqualifies
// the hole from ANY bonus regardless of the partner's score.
const nettQualityLevel = (nettScore, par) => {
    const diff = nettScore - par;
    if (diff > 0) return null;
    if (diff === 0) return 0;
    if (diff === -1) return 1;
    return 2;
};

// level1 + level2 + 1 reproduces every documented tier exactly:
//   par+par=1, par+birdie=2, birdie+birdie=3, eagle+birdie=4, eagle+eagle=5
// (par+eagle derives to 3, same as birdie+birdie - not explicitly listed in
// the rules but falls naturally out of the pattern the listed tiers form).
const calculateBonusPoints = (nett1, nett2, par) => {
    const level1 = nettQualityLevel(nett1, par);
    const level2 = nettQualityLevel(nett2, par);
    return level1 === null || level2 === null ? 0 : level1 + level2 + 1;
};

const buildFinaleEntry = (item, hole, handicap1, handicap2, pointFor) => {
    const p1 = computeHolePoints(hole.player1Score || 0, handicap1, item, pointFor);
    const p2 = computeHolePoints(hole.player2Score || 0, handicap2, item, pointFor);

    const hasP1 = p1.grossScore !== 0;
    const hasP2 = p2.grossScore !== 0;

    const player1Point = hasP1 ? p1.point : 0;
    const player2Point = hasP2 ? p2.point : 0;

    // "Worst nett score counts" - compare nett-relative-to-par directly
    // (higher diff = worse), not just Stableford points, so this stays
    // correct even if the point table is ever non-monotonic.
    let countedPlayer = null;
    let stablefordPoints = 0;
    if (hasP1 && hasP2) {
        const diff1 = p1.netScore - item.Par;
        const diff2 = p2.netScore - item.Par;
        countedPlayer = diff1 >= diff2 ? 'player1' : 'player2';
        stablefordPoints = countedPlayer === 'player1' ? player1Point : player2Point;
    } else if (hasP1) {
        countedPlayer = 'player1';
        stablefordPoints = player1Point;
    } else if (hasP2) {
        countedPlayer = 'player2';
        stablefordPoints = player2Point;
    }

    const bonusPoints = (hasP1 && hasP2)
        ? calculateBonusPoints(p1.netScore, p2.netScore, item.Par)
        : 0;

    const totalScore = stablefordPoints + bonusPoints;

    return {
        HoleNo: item.HoleNoValue,
        player1GrossScore: p1.grossScore, player2GrossScore: p2.grossScore,
        player1NetScore: p1.netScore, player2NetScore: p2.netScore,
        player1Point, player2Point,
        stablefordPoints, bonusPoints,
        maxTotal: totalScore,
        player1Scratch: p1.IsScratch, player2Scratch: p2.IsScratch,
        countedPlayer, // whose (worse) nett counted for the team
    };
};
//#endregion

const DevilsDoubles666 = ({
    playerList,
    tournamentDetails,
    tournamentDraw,
    tournamentRound,
    selectedPlayer,
    gameDetails,
    currentRound,
    format,
    scorecardStatus,
    setScorecardStatus,
    pointData,
    setShowPointModal,
    closeDrawer,
    fetchTournamentLeaderboard
}) => {

    const [scores,            setScores]            = useState(null);
    const [totalScores,       setTotalScores]       = useState([]);
    const [footerDomainScore,      setFooterDomainScore]      = useState('-');
    const [footerDestructionScore, setFooterDestructionScore] = useState('-');
    const [footerFinaleScore,      setFooterFinaleScore]      = useState('-');
    const [footerTotalScore,       setFooterTotalScore]       = useState('-');
    const [comment,            setComment]            = useState("");

    // Each segment has its own handicap, computed differently (see
    // getCombinedHandicap / getCappedIndividualHandicap above).
    const [calculatedHandicapDomain,      setCalculatedHandicapDomain]      = useState();
    const [calculatedHandicapDestruction, setCalculatedHandicapDestruction] = useState();
    const [calculatedHandicapFinale1,     setCalculatedHandicapFinale1]     = useState();
    const [calculatedHandicapFinale2,     setCalculatedHandicapFinale2]     = useState();

    // Holes (7-12) nominated by the team for double points in Devil's
    // Destruction - max 3, loaded/persisted via the SAME
    // getTournamentHoleSelection / saveTournamentHoleSelection APIs as
    // TournamentHolePlayerCheck.jsx. Team-level (one flag per hole), not
    // per-player, since Destruction has a single combined team score - each
    // saved row is still recorded against Player1 (PlayerNo: 1) so it uses
    // the exact same payload shape as TournamentHolePlayerCheck, it's just
    // that PlayerNo isn't meaningful to read back here (see loadPreferredHoles).
    const [preferredHoleChecks,      setPreferredHoleChecks]      = useState(buildEmptyPreferredHoleChecks());
    const [savedPreferredHoleChecks, setSavedPreferredHoleChecks] = useState(buildEmptyPreferredHoleChecks());
    const [preferredHolesLoading,    setPreferredHolesLoading]    = useState(false);
    const [preferredHolesSaving,     setPreferredHolesSaving]     = useState(false);
    const [showPreferredHolesModal,  setShowPreferredHolesModal]  = useState(false);

    const inputRefs = useRef([]);

    //#region Per-segment format/handicap config
    // const scrambleFormatData = useMemo(
    //     () => getRoundFormatConfig(format?.RoundFormat, 'Scramble', tournamentDraw),
    //     [format, tournamentDraw]
    // );
    // const foursomeFormatData = useMemo(
    //     () => getRoundFormatConfig(format?.RoundFormat, 'Foursomes', tournamentDraw),
    //     [format, tournamentDraw]
    // );
    // const worstBallFormatData = useMemo(
    //     () => getRoundFormatConfig(format?.RoundFormat, 'Worstball', tournamentDraw),
    //     [format, tournamentDraw]
    // );
    const {
        scramble: scrambleFormatData,
        foursomes: foursomeFormatData,
        worstball: worstBallFormatData,
    } = useMemo(() => {
        const formatMap = buildRoundFormatMap(format?.RoundFormat);
        return {
            scramble: getRoundFormatConfig(formatMap, 'Scramble', tournamentDraw),
            foursomes: getRoundFormatConfig(formatMap, 'Foursomes', tournamentDraw),
            worstball: getRoundFormatConfig(formatMap, 'Worstball', tournamentDraw),
        };
    }, [ format?.RoundFormat, tournamentDraw]);
    //#endregion

    //#region Preferred holes (Destruction double points)
    // Only the SAVED (persisted) selection drives actual scoring/doubling -
    // in-progress edits inside the modal don't double points until Saved,
    // same "click Save to make it permanent" behavior as TournamentHolePlayerCheck.
    const preferredHoles = useMemo(
        () => new Set(
            Object.entries(savedPreferredHoleChecks)
                .filter(([, checked]) => checked)
                .map(([holeNo]) => Number(holeNo))
        ),
        [savedPreferredHoleChecks]
    );

    const preferredHoleCount = Object.values(preferredHoleChecks).filter(Boolean).length;
    const isPreferredHolesDirty = JSON.stringify(preferredHoleChecks) !== JSON.stringify(savedPreferredHoleChecks);

    const loadPreferredHoles = async () => {
        if (!tournamentDraw?.ID) return;
        setPreferredHolesLoading(true);
        try {
            const data = await getTournamentHoleSelection(tournamentDraw.ID);
            const hydrated = buildEmptyPreferredHoleChecks();
            (data || []).forEach((row) => {
                // Team-level: any row present for a hole in the 7-12 range
                // means that hole is doubled for this pairing, regardless of
                // which PlayerNo it was recorded against.
                if (hydrated.hasOwnProperty(row.Hole)) hydrated[row.Hole] = true;
            });
            setPreferredHoleChecks(hydrated);
            setSavedPreferredHoleChecks(hydrated);
        } catch (err) {
            swal.fire({ icon: 'error', titleText: 'Error!', text: 'Could not load preferred holes' });
        } finally {
            setPreferredHolesLoading(false);
        }
    };

    useEffect(() => { loadPreferredHoles(); }, [tournamentDraw]);

    // Local-only toggle, same as TournamentHolePlayerCheck's handleToggle -
    // takes effect on the scorecard only after handleSavePreferredHoles runs.
    const togglePreferredHole = (holeNo) => {
        const isCurrentlyChecked = preferredHoleChecks[holeNo];

        if (!isCurrentlyChecked && preferredHoleCount >= MAX_PREFERRED_HOLES) {
            swal.fire({ icon: 'warning', titleText: 'Limit Reached', text: `Only ${MAX_PREFERRED_HOLES} holes can be selected for double points.` });
            return;
        }

        setPreferredHoleChecks((prev) => ({ ...prev, [holeNo]: !isCurrentlyChecked }));
    };

    const handleSavePreferredHoles = async () => {
        const selections = Object.entries(preferredHoleChecks)
            .filter(([, checked]) => checked)
            .map(([holeNo]) => ({
                Hole: Number(holeNo),
                PlayerNo: 1,
                PlayerID: selectedPlayer.PlayerID,
                PlayerName: selectedPlayer.Player1Nick || selectedPlayer.Player1,
                PlayerType: selectedPlayer.Player1Type,
            }));

        setPreferredHolesSaving(true);
        try {
            const data  = await saveTournamentHoleSelection({
                tournamentDrawID: tournamentDraw.ID,
                selections,
            });
            // NOTE: TournamentHolePlayerCheck.jsx has `if (!data?.Message == 'Success')`
            // here, which due to operator precedence always evaluates to
            // false (never throws) regardless of the actual response - worth
            // fixing there too. Written correctly below.
            if (data?.Message !== 'success') throw new Error(data?.Message || 'Could not save');

            setSavedPreferredHoleChecks(preferredHoleChecks);
            swal.fire({ icon: 'success', titleText: 'Saved', timer: 1200, showConfirmButton: false, animation: false });
        } catch (err) {
            swal.fire({ icon: 'error', titleText: 'Error!', text: err.message || 'Something went wrong' });
        } finally {
            setPreferredHolesSaving(false);
        }
    };

    const handleClosePreferredHolesModal = () => {
        if (!isPreferredHolesDirty) { setShowPreferredHolesModal(false); return; }
        swal.fire({
            icon: 'warning',
            titleText: 'Discard unsaved changes?',
            showCancelButton: true,
            confirmButtonText: 'Discard',
        }).then((result) => {
            if (result.isConfirmed) {
                setPreferredHoleChecks(savedPreferredHoleChecks); // revert
                setShowPreferredHolesModal(false);
            }
        });
    };
    //#endregion

    //#region Effects
    useEffect(() => {
        if (!selectedPlayer) return;

        setCalculatedHandicapDomain(
            getAdjustedHandicap( scrambleFormatData?.Higher, scrambleFormatData?.Lower, selectedPlayer.Handicap, selectedPlayer.Player2Handicap)
        );
        setCalculatedHandicapDestruction(
            getAdjustedHandicap( foursomeFormatData?.Higher, foursomeFormatData?.Lower, selectedPlayer.Handicap, selectedPlayer.Player2Handicap)
        );
        setCalculatedHandicapFinale1(// worstBallFormatData
            getCappedIndividualHandicap(worstBallFormatData?.Higher, worstBallFormatData?.Lower, selectedPlayer.Player1Handicap, selectedPlayer.Player1Gender)
        );
        setCalculatedHandicapFinale2(
            getCappedIndividualHandicap(worstBallFormatData?.Higher, worstBallFormatData?.Lower, selectedPlayer.Player2Handicap, selectedPlayer.Player2Gender)
        );
    }, [selectedPlayer, scrambleFormatData, foursomeFormatData, worstBallFormatData]);

    useEffect(() => {
        if (!gameDetails?.TeeBoxes?.ScoreCard) return;

        const initialScores = {};

        if (gameDetails.Card.length > 0) {
            gameDetails.Card.forEach((item) => {
                const index = item.HoleValue - 1;
                const segment = getSegmentForHole(index);
                const existing = initialScores[index];
                let newScore;

                if (segment === 'finale') {
                    const scoreKey = item.PlayerID === selectedPlayer.PlayerID ? 'player1Score' : 'player2Score';
                    newScore = { [scoreKey]: item.Scratch == "Y" ? "X" : item.Score };
                } else {
                    newScore = {
                        player1Score: item.Scratch == "Y" ? "X" : item.Score,
                        driveUserID: item.DriveUserID || selectedPlayer.PlayerID
                    };
                }

                initialScores[index] = existing ? { ...existing, ...newScore } : newScore;
            });

            for (let i = 0; i < 18; i++) {
                const segment = getSegmentForHole(i);
                if (!initialScores[i]) {
                    initialScores[i] = segment === 'finale'
                        ? { player1Score: 0, player2Score: 0 }
                        : { player1Score: 0, driveUserID: selectedPlayer.PlayerID };
                } else if (segment === 'finale') {
                    if (!initialScores[i].hasOwnProperty('player1Score')) initialScores[i].player1Score = 0;
                    if (!initialScores[i].hasOwnProperty('player2Score')) initialScores[i].player2Score = 0;
                } else {
                    if (!initialScores[i].hasOwnProperty('player1Score')) initialScores[i].player1Score = 0;
                    if (!initialScores[i].hasOwnProperty('driveUserID')) initialScores[i].driveUserID = selectedPlayer.PlayerID;
                }
            }
        } else {
            gameDetails.TeeBoxes.ScoreCard.forEach((item, index) => {
                const segment = getSegmentForHole(index);
                initialScores[index] = segment === 'finale'
                    ? { player1Score: 0, player2Score: 0 }
                    : { player1Score: 0, driveUserID: selectedPlayer.PlayerID };
            });
        }

        if (!!gameDetails?.Result) {
            let scStatus = (gameDetails?.Result.IsVerified == 'Y' ? 'verify' : (gameDetails?.Result.IsVerified == 'DQ' ? 'disqualify' : (gameDetails?.Result.IsVerified == 'WD' ? 'withdraw' : 'N')));
            setScorecardStatus(scStatus);
            setComment(gameDetails?.Result.Comment);
        }

        setScores(initialScores);
    }, [gameDetails, selectedPlayer]);

    useEffect(() => {
        if (scores) calculateScores(scores);
    }, [scores, calculatedHandicapDomain, calculatedHandicapDestruction, calculatedHandicapFinale1, calculatedHandicapFinale2, preferredHoles]);

    useEffect(() => {
        if (selectedPlayer) {
            setScorecardStatus(selectedPlayer.IsVerified == 'Y' ? "verify" : selectedPlayer.IsVerified == "WD" ? 'withdraw' : selectedPlayer.IsVerified == 'DQ' ? 'disqualify' : '');
        }
    }, [selectedPlayer]);
    //#endregion

    //#region API's
    const deleteScoreCard = async () => {
        try {
            swal.fire({ titleText: 'Loading', text: 'Please Wait...', showConfirmButton: false, onOpen: () => swal.showLoading() });
            await deleteScorecard(selectedPlayer.DrawID);
            fetchTournamentLeaderboard();
            swal.fire({ icon: 'success', timer: 1500, animation: false, showConfirmButton: false, onOpen: () => swal.hideLoading(), titleText: 'Scorecard Updated Successfully!' });
            closeDrawer();
        } catch (err) {
            swal.fire({ icon: 'error', buttonsStyling: false, confirmButtonClass: 'btn btn-brand', titleText: 'Error!', text: err.message, onOpen: () => swal.hideLoading() });
        }
    }

    const postScorecard = async (scoreCard) => {
        try {
            swal.fire({ titleText: 'Loading', showConfirmButton: false, text: 'Please Wait...', onOpen: () => swal.showLoading() });
            await postScoreCard(scoreCard);
            fetchTournamentLeaderboard();
            swal.fire({ icon: 'success', timer: 1500, animation: false, showConfirmButton: false, onOpen: () => swal.hideLoading(), titleText: 'Scorecard Updated Successfully!' });
            closeDrawer();
        } catch (err) {
            swal.fire({ icon: 'error', buttonsStyling: false, confirmButtonClass: 'btn btn-brand', titleText: 'Error!', text: err.message, onOpen: () => swal.hideLoading() });
        }
    }
    //#endregion

    //#region Helpers
    const handleDriveUserChange = (index, value) => {
        setScores(s => ({ ...s, [index]: { ...s[index], driveUserID: value } }));
    }

    const calculateScores = (updatedScores) => {
        const pointFor = createPointFor(pointData);

        const updatedTotalScores = [];
        let footerDomain = 0, footerDestruction = 0, footerFinale = 0;
        let allDomainZero = true, allDestructionZero = true, allFinaleZero = true;

        gameDetails.TeeBoxes.ScoreCard.forEach((item, index) => {
            const segment = getSegmentForHole(index);
            const hole = updatedScores[index] || {};
            let entry;

            if (segment === 'domain') {
                entry = buildCombinedScoreEntry(item, hole, calculatedHandicapDomain, pointFor, false, selectedPlayer);
            } else if (segment === 'destruction') {
                // Drive is manually picked on every hole (hole.driveUserID),
                // same as Domain - no auto-alternation.
                const isDoubled = preferredHoles.has(item.HoleNoValue);
                entry = buildCombinedScoreEntry(item, hole, calculatedHandicapDestruction, pointFor, isDoubled, selectedPlayer);
            } else {
                entry = buildFinaleEntry(item, hole, calculatedHandicapFinale1, calculatedHandicapFinale2, pointFor);
            }

            entry.segment = segment;

            if (entry.maxTotal != 0) {
                if (segment === 'domain') { allDomainZero = false; footerDomain += entry.maxTotal; }
                else if (segment === 'destruction') { allDestructionZero = false; footerDestruction += entry.maxTotal; }
                else { allFinaleZero = false; footerFinale += entry.maxTotal; }
            }

            updatedTotalScores[index] = entry;
        });

        setTotalScores(updatedTotalScores);
        setFooterDomainScore(allDomainZero ? '-' : footerDomain);
        setFooterDestructionScore(allDestructionZero ? '-' : footerDestruction);
        setFooterFinaleScore(allFinaleZero ? '-' : footerFinale);
        const allZero = allDomainZero && allDestructionZero && allFinaleZero;
        setFooterTotalScore(allZero ? '-' : (footerDomain + footerDestruction + footerFinale));
    };

    const handleScoreChange = (playerKey, index, value) => {
        if (!(/^[0-9X]*$/).test(value)) return;

        const oldScore = scores[index];
        const updatedScores = { ...scores, [index]: { ...oldScore, [playerKey]: value } };
        setScores(updatedScores);

        const segment = getSegmentForHole(index);
        let nextKey;
        if (segment === 'finale' && playerKey === 'player1Score') {
            nextKey = `${index}_player2Score`;
        } else {
            nextKey = `${index + 1}_player1Score`;
        }
        if (inputRefs.current[nextKey]) {
            inputRefs.current[nextKey].focus();
            inputRefs.current[nextKey].select();
        }
    };

    const toggleStatus = (value) => setScorecardStatus(prevStatus => (prevStatus == value ? '' : value));

    const saveScorecard = () => {

        if (scorecardStatus === 'verify') {
            if (preferredHoles.size < MAX_PREFERRED_HOLES) {
                swal.fire({
                    icon: 'warning',
                    titleText: 'Preferred Holes Not Selected',
                    text: `Please select and save ${MAX_PREFERRED_HOLES} Preferred Holes (7-12) for double points before verifying the scorecard.`
                });
                return;
            }
        }
        let newScoreCard = [];
        let notComplete = false;
        let hole = 0;

        gameDetails.TeeBoxes.ScoreCard.forEach((item, index) => {
            const segment = getSegmentForHole(index);
            const cHole = item.HoleNoValue;
            let cScore = totalScores.find(t => t.HoleNo == cHole);
            if (!cScore) { notComplete = true; return; }

            if (segment === 'finale') {
                if (((isNaN(cScore.player1GrossScore) || cScore.player1GrossScore == 0) || (isNaN(cScore.player2GrossScore) || cScore.player2GrossScore == 0)) && scorecardStatus == 'verify') {
                    notComplete = true;
                    return;
                }

                // Each row carries that specific player's OWN Stableford
                // points (same convention as WorstballGreensome) - the
                // hole's bonus points are hole-level (apply to the team, not
                // one player), so they're duplicated on both rows via a
                // separate BonusPoint field rather than folded into Point.
                newScoreCard.push({
                    ID: 0, TournamentDrawID: selectedPlayer.DrawID,
                    PlayerID: selectedPlayer.PlayerID, PlayerType: selectedPlayer.Player1Type,
                    ScoreCardMasterID: item.ID,
                    Score: cScore.player1GrossScore, HandicapScore: cScore.player1NetScore,
                    DriveUserID: 0, Point: cScore.player1Point, TotalPoint: cScore.player1Point,
                    BonusPoint: cScore.bonusPoints, WorstNettCounted: cScore.countedPlayer === 'player1' ? 'Y' : 'N',
                    Scratch: (cScore.player1Scratch) ? "Y" : "N",
                });

                newScoreCard.push({
                    ID: 0, TournamentDrawID: selectedPlayer.DrawID,
                    PlayerID: selectedPlayer.Player2ID, PlayerType: selectedPlayer.Player2Type,
                    ScoreCardMasterID: item.ID,
                    Score: cScore.player2GrossScore, HandicapScore: cScore.player2NetScore,
                    DriveUserID: 0, Point: cScore.player2Point, TotalPoint: cScore.player2Point,
                    BonusPoint: cScore.bonusPoints, WorstNettCounted: cScore.countedPlayer === 'player2' ? 'Y' : 'N',
                    Scratch: (cScore.player2Scratch) ? "Y" : "N",
                });

                if (cScore.player1GrossScore != 0 || cScore.player2GrossScore != 0) hole++;
            } else {
                if ((isNaN(cScore.player1GrossScore) || cScore.player1GrossScore == 0) && scorecardStatus == 'verify') {
                    notComplete = true;
                    return;
                }

                newScoreCard.push({
                    ID: 0, TournamentDrawID: selectedPlayer.DrawID,
                    PlayerID: selectedPlayer.PlayerID, PlayerType: selectedPlayer.Player1Type,
                    ScoreCardMasterID: item.ID,
                    Score: cScore.player1GrossScore, HandicapScore: cScore.player1NetScore,
                    DriveUserID: cScore.driveUserID, Point: cScore.player1Point, TotalPoint: cScore.player1Point,
                    Doubled: cScore.doubled ? 'Y' : 'N',
                    Scratch: (cScore.player1Scratch) ? "Y" : "N",
                });

                if (cScore.player1GrossScore != 0) hole++;
            }
        });

        if (notComplete) {
            swal.fire({ icon: "warning", titleText: "Incomplete Scorecard", text: "Please complete the scorecard..!" });
            return;
        }

        const result = {
            ID: 0, Hole: hole, TournamentDrawID: selectedPlayer.DrawID,
            Point: footerTotalScore == '-' ? 0 : footerTotalScore,
            TotalPoint: footerTotalScore == '-' ? 0 : footerTotalScore,
            IsVerified: (scorecardStatus == 'verify') ? 'Y' : (scorecardStatus == 'disqualify' ? 'DQ' : (scorecardStatus == 'withdraw' ? 'WD' : 'N')),
            Comment: (scorecardStatus == 'disqualify' || scorecardStatus == 'withdraw') ? comment : '',
        };

        const dto = { scoreCard: newScoreCard, result: result };
        postScorecard(dto);
    }

    const sendScorecard = async () => {
        try {
            swal.fire({ showConfirmButton: false, onOpen: () => swal.showLoading(), titleText: 'Loading', text: 'Please Wait...', allowOutsideClick: false, allowEscapeKey: false });
 
            let playerNameList = [];
            playerNameList.push({
                ID: 1,
                Name: `${playerList["Player1"].UserName} | ${playerList["Player2"].UserName} - Domain ${calculatedHandicapDomain} / Destruction ${calculatedHandicapDestruction} / Finale ${calculatedHandicapFinale1}+${calculatedHandicapFinale2}`,
                Email: playerList["Player1"].Email + ';' + playerList["Player2"].Email
            });
 
            let scoreCardList = [];
            gameDetails.TeeBoxes.ScoreCard.forEach((item, index) => {
                const cHole = item.HoleNoValue;
                let cScore = totalScores.find(t => t.HoleNo == cHole);
                if (!cScore) return;
 
                // Drive name only means anything on Domain/Destruction holes
                // (single combined score, one of the two players drove);
                // Finale has no drive concept (both play their own ball).
                const driveName = (cScore.segment === 'domain' || cScore.segment === 'destruction')
                    ? (cScore.driveUserID == selectedPlayer.PlayerID ? selectedPlayer.Player1 : selectedPlayer.Player2)
                    : '-';
 
                scoreCardList.push({
                    Hole: cHole, Par: item.Par, Stroke: item.Stroke, Format: cScore.segment,
                    Drive: driveName,
                    Player1GrossScore: cScore.player1GrossScore, Player1NetScore: cScore.player1NetScore, Player1Point: cScore.player1Point,
                    Player2GrossScore: cScore.player2GrossScore ?? '-', Player2NetScore: cScore.player2NetScore ?? '-', Player2Point: cScore.player2Point ?? '-',
                    BonusPoints: cScore.bonusPoints || 0,
                    Doubled: cScore.doubled ? 'Yes (x2)' : 'No',
                    CountedPlayer: cScore.countedPlayer, // Finale only - which (worse) score counted
                    Total: cScore.maxTotal
                });
            });
 
            const tournament = {
                ID: tournamentDetails.ID, Name: tournamentDetails.Name,
                Format: format?.Format, Round: selectedPlayer.Round, Date: tournamentDetails.StartDate
            };
 
            await sendDevilsDoubles666ScorecardMail(
                playerNameList,
                selectedPlayer.Player1, selectedPlayer.Player2,
                scoreCardList,
                { domain: calculatedHandicapDomain, destruction: calculatedHandicapDestruction, finale1: calculatedHandicapFinale1, finale2: calculatedHandicapFinale2 },
                footerDomainScore, footerDestructionScore, footerFinaleScore, footerTotalScore == '-' ? 0 : footerTotalScore,
                tournament
            );
 
            swal.close();
            swal.fire({ icon: 'success', titleText: 'Scorecard Email Sent', showConfirmButton: false, timer: 1500 });
        } catch (err) {
            swal.fire({ icon: 'error', titleText: err.message, onOpen: () => swal.hideLoading() });
        }
    }
    //#endregion

    //#region Render helpers - Devil's Domain (Scramble UI)
    const renderDomainHead = () => (
        <>
            <tr>
                <th rowSpan={2} style={{ textAlign: 'center', width: '10%', verticalAlign: 'middle' }}>Hole</th>
                <th rowSpan={2} style={{ textAlign: 'center', width: '10%', verticalAlign: 'middle' }}>Par</th>
                <th rowSpan={2} style={{ textAlign: 'center', width: '10%', verticalAlign: 'middle' }}>Stroke</th>
                <th rowSpan={2} style={{ textAlign: 'center', width: '15%', verticalAlign: 'middle' }}>Drive</th>
                <th colSpan={3} style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                    {selectedPlayer.Player1Nick || selectedPlayer.Player1} | {selectedPlayer.Player2Nick || selectedPlayer.Player2} ({calculatedHandicapDomain}){(scrambleFormatData?.Lower != 100) || (scrambleFormatData?.Higher != 100) ? '*' : ''}
                </th>
                <th rowSpan={2} style={{ textAlign: 'center', width: '10%', verticalAlign: 'middle' }}>Total Score</th>
            </tr>
            <tr>
                <th className="text-center">Gross Score</th><th className="text-center">Net Score</th><th className="text-center">Points</th>
            </tr>
        </>
    );

    const renderDomainRow = (index, item) => {
        if (!scores || !scores[index]) return null;
        const totalScore = totalScores[index] || {};
        const player1Score = scores[index]?.player1Score || 0;
        const extraShot = getStrokeExtraShot(item.Stroke, calculatedHandicapDomain) || 0;
        const driveUserID = scores[index]?.driveUserID || selectedPlayer.PlayerID;

        return (
            <tr key={`row_${item.HoleNoValue}`} className="row-score" data-masterid={item.ID}>
                <td className='text-center'>{item.HoleNoValue}</td>
                <td className='text-center'>{item.Par}</td>
                <td className='text-center'>{item.Stroke}</td>
                <td>
                    <select className="form-drive form-control" tabIndex="-1"
                        onChange={(e) => handleDriveUserChange(index, e.target.value)}
                        value={driveUserID}>
                        <option value={selectedPlayer.PlayerID}>{selectedPlayer.Player1}</option>
                        <option value={selectedPlayer.Player2ID}>{selectedPlayer.Player2}</option>
                    </select>
                </td>
                <td className="player-td" data-extra={extraShot}>
                    <div className="input-group">
                        <input className="form-control input-sm input-numeric" type="text" value={player1Score}
                            ref={el => inputRefs.current[`${index}_player1Score`] = el}
                            onChange={(e) => handleScoreChange('player1Score', index, parseInt(e.target.value))} />
                        <div className="input-group-append">
                            <span className="input-group-text clickable" onClick={() => handleScoreChange('player1Score', index, "X")}><i className="la la-times"></i></span>
                        </div>
                    </div>
                </td>
                <td className="card-score text-center">{totalScore.player1NetScore}</td>
                <td className="card-point text-center">{totalScore.player1Point}</td>
                <td className="card-total-score text-center">{totalScore.maxTotal}</td>
            </tr>
        );
    };
    //#endregion

    //#region Render helpers - Devil's Destruction (Foursome UI + auto drive + doubling)
    const renderDestructionHead = () => (
        <>
            <tr>
                <th rowSpan={2} style={{ textAlign: 'center', width: '10%', verticalAlign: 'middle' }}>Hole</th>
                <th rowSpan={2} style={{ textAlign: 'center', width: '10%', verticalAlign: 'middle' }}>Par</th>
                <th rowSpan={2} style={{ textAlign: 'center', width: '10%', verticalAlign: 'middle' }}>Stroke</th>
                <th rowSpan={2} style={{ textAlign: 'center', width: '15%', verticalAlign: 'middle' }}>Drive</th>
                <th colSpan={3} style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                    {selectedPlayer.Player1Nick || selectedPlayer.Player1} | {selectedPlayer.Player2Nick || selectedPlayer.Player2} ({calculatedHandicapDestruction}){(foursomeFormatData?.Lower != 100) || (foursomeFormatData?.Higher != 100) ? '*' : ''}
                </th>
                <th rowSpan={2} style={{ textAlign: 'center', width: '10%', verticalAlign: 'middle' }}>Total Score</th>
            </tr>
            <tr>
                <th className="text-center">Gross Score</th><th className="text-center">Net Score</th><th className="text-center">Points</th>
            </tr>
        </>
    );

    const renderDestructionRow = (index, item) => {
        if (!scores || !scores[index]) return null;
        const totalScore = totalScores[index] || {};
        const player1Score = scores[index]?.player1Score || 0;
        const extraShot = getStrokeExtraShot(item.Stroke, calculatedHandicapDestruction) || 0;
        const driveUserID = scores[index]?.driveUserID || selectedPlayer.PlayerID;

        return (
            <tr key={`row_${item.HoleNoValue}`} className="row-score" data-masterid={item.ID}>
                <td className='text-center'>{item.HoleNoValue}</td>
                <td className='text-center'>{item.Par}</td>
                <td className='text-center'>{item.Stroke}</td>
                <td>
                    <select className="form-drive form-control" tabIndex="-1"
                        onChange={(e) => handleDriveUserChange(index, e.target.value)}
                        value={driveUserID}>
                        <option value={selectedPlayer.PlayerID}>{selectedPlayer.Player1}</option>
                        <option value={selectedPlayer.Player2ID}>{selectedPlayer.Player2}</option>
                    </select>
                </td>
                <td className="player-td" data-extra={extraShot}>
                    <div className="input-group">
                        <input className="form-control input-sm input-numeric" type="text" value={player1Score}
                            ref={el => inputRefs.current[`${index}_player1Score`] = el}
                            onChange={(e) => handleScoreChange('player1Score', index, parseInt(e.target.value))} />
                        <div className="input-group-append">
                            <span className="input-group-text clickable" onClick={() => handleScoreChange('player1Score', index, "X")}><i className="la la-times"></i></span>
                        </div>
                    </div>
                </td>
                <td className="card-score text-center">{totalScore.player1NetScore}</td>
                <td className="card-point text-center">
                    {totalScore.player1Point}
                    {totalScore.doubled && <sup className="text-danger font-weight-bold"> x2</sup>}
                </td>
                <td className="card-total-score text-center">{totalScore.maxTotal}</td>
            </tr>
        );
    };
    //#endregion

    //#region Render helpers - Devil's Finale (Worstball UI, worst-nett + bonus)
    const renderFinaleHead = () => (
        <>
            <tr>
                <th rowSpan={2} style={{ textAlign: 'center', width: '8%', verticalAlign: 'middle' }}>Hole</th>
                <th rowSpan={2} style={{ textAlign: 'center', width: '8%', verticalAlign: 'middle' }}>Par</th>
                <th rowSpan={2} style={{ textAlign: 'center', width: '8%', verticalAlign: 'middle' }}>Stroke</th>
                <th colSpan={3} style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                    {selectedPlayer.Player1} ({calculatedHandicapFinale1 < 0 ? "+" + Math.abs(calculatedHandicapFinale1) : calculatedHandicapFinale1}) {(worstBallFormatData?.Lower != 100) || (worstBallFormatData?.Higher != 100) ? '*' : ''}
                </th>
                <th colSpan={3} style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                    {selectedPlayer.Player2} ({calculatedHandicapFinale2 < 0 ? "+" + Math.abs(calculatedHandicapFinale2) : calculatedHandicapFinale2}) {(worstBallFormatData?.Lower != 100) || (worstBallFormatData?.Higher != 100) ? '*' : ''}
                </th>
                <th rowSpan={2} style={{ textAlign: 'center', width: '8%', verticalAlign: 'middle' }}>Bonus</th>
                <th rowSpan={2} style={{ textAlign: 'center', width: '8%', verticalAlign: 'middle' }}>Total Score</th>
            </tr>
            <tr>
                <th className="text-center">Gross Score</th><th className="text-center">Net Score</th><th className="text-center">Points</th>
                <th className="text-center">Gross Score</th><th className="text-center">Net Score</th><th className="text-center">Points</th>
            </tr>
        </>
    );

    const renderFinaleRow = (index, item) => {
        if (!scores || !scores[index]) return null;
        const totalScore = totalScores[index] || {};
        const player1Score = scores[index]?.player1Score || 0;
        const player2Score = scores[index]?.player2Score || 0;
        const player1ExtraShot = getStrokeExtraShot(item.Stroke, calculatedHandicapFinale1) || 0;
        const player2ExtraShot = getStrokeExtraShot(item.Stroke, calculatedHandicapFinale2) || 0;

        // "Worst nett counts" - highlight whichever player's score is the
        // one actually hurting the team, so it reads clearly at a glance
        // (opposite convention from Worstball's "best counts" highlight).
        const player1Counted = totalScore.countedPlayer === 'player1';
        const player2Counted = totalScore.countedPlayer === 'player2';

        return (
            <tr key={`row_${item.HoleNoValue}`} className="row-score" data-masterid={item.ID}>
                <td className='text-center'>{item.HoleNoValue}</td>
                <td className='text-center'>{item.Par}</td>
                <td className='text-center'>{item.Stroke}</td>
                <td className={`player-td ${player1Counted ? 'bg-light-danger' : ''}`} data-extra={player1ExtraShot} title={player1Counted ? "This player's (worse) score counted for the team" : ''}>
                    <div className="input-group">
                        <input className="form-control input-sm input-numeric" type="text" value={player1Score}
                            ref={el => inputRefs.current[`${index}_player1Score`] = el}
                            onChange={(e) => handleScoreChange('player1Score', index, parseInt(e.target.value))} />
                        <div className="input-group-append">
                            <span className="input-group-text clickable" onClick={() => handleScoreChange('player1Score', index, "X")}><i className="la la-times"></i></span>
                        </div>
                    </div>
                </td>
                <td className={`card-score text-center`}>{totalScore.player1NetScore}</td>
                <td className="card-point text-center">
                    {player1Counted ? (
                        <span className="badge badge-success font-weight-bold">
                        {totalScore.player1Point}
                        </span>
                    ) : (
                        totalScore.player1Point
                    )}
                </td>
                <td className={`player-td ${player2Counted ? 'bg-light-danger' : ''}`} data-extra={player2ExtraShot} title={player2Counted ? "This player's (worse) score counted for the team" : ''}>
                    <div className="input-group">
                        <input className="form-control input-sm input-numeric" type="text" value={player2Score}
                            ref={el => inputRefs.current[`${index}_player2Score`] = el}
                            onChange={(e) => handleScoreChange('player2Score', index, parseInt(e.target.value))} />
                        <div className="input-group-append">
                            <span className="input-group-text clickable" onClick={() => handleScoreChange('player2Score', index, "X")}><i className="la la-times"></i></span>
                        </div>
                    </div>
                </td>
                <td className={`card-score text-center`}>{totalScore.player2NetScore}</td>
                <td className="card-point text-center">
                    {player2Counted ? (
                        <span className="badge badge-success font-weight-bold">
                        {totalScore.player2Point}
                        </span>
                    ) : (
                        totalScore.player2Point
                    )}
                </td>
                <td className="text-center">
                    {totalScore.bonusPoints > 0
                        ? <span className="badge badge-warning" title="Bonus points for nett par-or-better by both players">+{totalScore.bonusPoints}</span>
                        : '-'}
                </td>
                <td className="card-total-score text-center">{totalScore.maxTotal}</td>
            </tr>
        );
    };
    //#endregion

    //#region Generic segment table wrapper
    const renderSegmentTable = (holeIndices, title, subtitle, footerScore, renderHead, renderRow, colCount) => {
        if (!gameDetails || !scores) return null;

        return (
            <div className="table-register mb-4 mt-4">
                <div className="d-flex justify-content-between align-items-center mb-2">
                    <h6 className="mb-0 font-weight-bold">{title} <span className="text-muted">({subtitle})</span></h6>
                </div>
                <table id="table-scorecard" className="table table-bordered table-striped table-card">
                    <thead>{renderHead()}</thead>
                    <tbody>
                        {holeIndices.map((idx) => renderRow(idx, gameDetails.TeeBoxes.ScoreCard[idx]))}
                    </tbody>
                    <tfoot>
                        <tr className="bg-light font-weight500">
                            <td className='text-center'>OUT</td>
                            <td className='text-center'>
                                {holeIndices.reduce((sum, i) => sum + (gameDetails.TeeBoxes.ScoreCard[i]?.Par || 0), 0)}
                            </td>
                            {Array.from({ length: colCount - 3 }).map((_, i) => <td key={i} className='text-center'></td>)}
                            <td className='text-center font-weight-bold'>{footerScore}</td>
                        </tr>
                    </tfoot>
                </table>
            </div>
        );
    };
    //#endregion

    if (!gameDetails || !scores) return null;

    return (<>
        <div className="kt-portlet kt-portlet--mobile padding-lr20">
            <div className="kt-portlet__head kt-portlet__head--lg d-block padding-lr0 border-0">

                <div id="roundInfoPanel" className="kt-notification margin-t10 mb-3" style={{ backgroundColor: "rgb(247, 248, 250)" }}>
                    <div className="kt-notification__item d-block">
                        <div className="row">
                            <div className="col-3">
                                <div className="kt-user-card-v2 mb-2">
                                    <div className="kt-user-card-v2__pic">
                                        <img id="txtTournamentImg" src={tournamentDetails?.Icon} alt="tournament-logo" />
                                    </div>
                                    <div className="kt-user-card-v2__details ">
                                        <span id="txtTournament" className="kt-user-card-v2__name text-clip"> {tournamentDetails?.Name} </span>
                                        <span className="kt-user-card-v2__desc ">
                                            <span id="txtRound"> Round {selectedPlayer?.Round} </span>
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <div className="col-2 d-flex-center">
                                <div className="kt-user-card-v2 ">
                                    <div className="kt-user-card-v2__details ">
                                        <span className="kt-user-card-v2__name ">Date </span>
                                        <span className="kt-user-card-v2__desc">
                                            <span id="txtStartDate">{moment(tournamentDetails?.StartDate).format('DD-MMM-YYYY')}</span>
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <div className="col-2 d-flex-center">
                                <div className="kt-user-card-v2">
                                    <div className="kt-user-card-v2__details ">
                                        <span className="kt-user-card-v2__name ">Format </span>
                                        <span className="kt-user-card-v2__desc ">
                                            <span id="txtFormat">{format?.Format || "Devil's Doubles (6-6-6)"}</span>
                                        </span>
                                    </div>
                                </div>
                            </div>
                            {!!tournamentRound && tournamentRound?.length > 0 && (
                                <div className="col-2 d-flex-center">
                                    <div className="kt-user-card-v2 mb-2">
                                        <div className="kt-user-card-v2__details ">
                                            <span className="kt-user-card-v2__name ">Tee Of Option </span>
                                            <span className="kt-user-card-v2__desc ">
                                                <span id="txtTeeOption">{format?.TeeOffOption}</span>
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            )}
                            <div className="col-1 d-flex-center">
                                <div className="kt-user-card-v2 ">
                                    <div className="kt-user-card-v2__details ">
                                        <span className="kt-user-card-v2__name " id="lbltxtSlot"> Slot </span>
                                        <span className="kt-user-card-v2__desc ">
                                            <span id="txtSlot">{selectedPlayer.Slot}</span>
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <div className="col-2 d-flex-center justify-content-end">
                                {!!pointData && pointData.length > 0 && (
                                    <button id="btnPoint" type="button" className="btn btn-secondary btn-elevate btn-pill btn-sm m-0 mr-2"
                                        onClick={() => setShowPointModal(true)}>
                                        <i className="la la-list"></i> Point
                                    </button>
                                )}
                                <button id="btnPreferredHoles" type="button" className="btn btn-secondary btn-elevate btn-pill btn-sm m-0"
                                    onClick={() => setShowPreferredHolesModal(true)}>
                                    <i className="la la-check-square"></i>Holes ({preferredHoles.size}/{MAX_PREFERRED_HOLES})
                                </button>
                            </div>
                        </div>

                        <hr />
                        <div className="row">
                            <div className="col-12">
                                <small className="text-muted">
                                    Holes 1-6: <strong>Devil's Domain</strong> (Scramble)
                                    &nbsp;|&nbsp;
                                    Holes 7-12: <strong>Devil's Destruction</strong> (Foursome)
                                    &nbsp;|&nbsp;
                                    Holes 13-18: <strong>Devil's Finale</strong> (Worstball - worst nett counts)
                                </small>
                            </div>
                        </div>
                        <hr />

                        <div className="row">
                            <div className="col-9">
                                <label className="kt-checkbox kt-checkbox--bold kt-checkbox--success mb-0">
                                    <input
                                        value="verify"
                                        name='verify'
                                        checked={scorecardStatus == 'verify'}
                                        onChange={() => toggleStatus('verify')}
                                        type="checkbox"
                                        className="form-verify" />
                                    Verify Scorecard <span></span>
                                </label>
                            </div>
                            <div className="col-3 text-right">
                                <div className="kt-checkbox-inline">
                                    <label className="kt-checkbox kt-checkbox--bold kt-checkbox--danger mb-0 mr-2">
                                        <input
                                            value="disqualify"
                                            name='disqualify'
                                            checked={scorecardStatus == 'disqualify'}
                                            onChange={() => toggleStatus('disqualify')}
                                            type="checkbox"
                                            className="form-disqualify" />
                                        Disqualify <span></span>
                                    </label>
                                    <label className="kt-checkbox kt-checkbox--bold kt-checkbox--brand mb-0">
                                        <input
                                            value="withdraw"
                                            checked={scorecardStatus == 'withdraw'}
                                            onChange={() => toggleStatus('withdraw')}
                                            name='withdraw'
                                            type="checkbox"
                                            className="form-withdraw" />
                                        Withdraw <span></span>
                                    </label>
                                </div>
                            </div>
                        </div>
                        {(scorecardStatus == 'withdraw' || scorecardStatus == 'disqualify') && (
                            <div className="row justify-content-end mt-2" id="pnlComment">
                                <div className="col-4 text-right">
                                    <textarea
                                        name="txtComment"
                                        id="txtComment"
                                        className="form-control"
                                        value={comment}
                                        onChange={(e) => setComment(e.target.value)}
                                        cols="3" rows="2" placeholder="Comments"
                                    ></textarea>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {renderSegmentTable(DOMAIN_HOLES, "Devil's Domain (1-6)", 'Scramble', footerDomainScore, renderDomainHead, renderDomainRow, 8)}
                {renderSegmentTable(DESTRUCTION_HOLES, "Devil's Destruction (7-12)", 'Foursome', footerDestructionScore, renderDestructionHead, renderDestructionRow, 8)}
                {renderSegmentTable(FINALE_HOLES, "Devil's Finale (13-18)", 'Worstball - worst nett counts', footerFinaleScore, renderFinaleHead, renderFinaleRow, 11)}

                <div className="row">
                    <div className="col-12 text-right">
                        <h5>Total: <span id="lblGrandTotal">{footerTotalScore}</span></h5>
                    </div>
                </div>
            </div>

            <div className="kt-portlet__foot">
                <div className="kt-form__actions">
                    <div className="row">
                        <div className="col-lg-12 ml-lg-auto text-center">
                            {(selectedPlayer.IsVerified == 'Y' || selectedPlayer.IsVerified == 'WD' || selectedPlayer.IsVerified == 'DQ') && (
                                <button id="btnEmailCard" type="button" className="btn btn-info mr-2" onClick={sendScorecard}><i className="fa fa-envelope"></i>Email Card </button>
                            )}
                            <button id="btnDelete" type="button" className="btn btn-danger d-inline-block mr-4" onClick={deleteScoreCard}> Delete </button>
                            <button id="btnSave" type="button" className="btn btn-success d-inline-block mr-2" onClick={saveScorecard}> Save </button>
                            <button id="btnBack" type="button" className="btn btn-secondary" onClick={closeDrawer}> Close </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        {showPreferredHolesModal && (
            <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex="-1" role="dialog">
                <div className="modal-dialog modal-dialog-centered" role="document">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">Preferred Holes</h5>
                            <button type="button" className="close" aria-label="Close" onClick={handleClosePreferredHolesModal}>
                                <span aria-hidden="true">&times;</span>
                            </button>
                        </div>
                        <div className="modal-body">
                            {preferredHolesLoading ? (
                                <div className="text-center p-4">Loading...</div>
                            ) : (
                                <>
                                    <p className="text-muted">Pick up to {MAX_PREFERRED_HOLES} holes from Devil's Destruction (Holes 7-12)</p>
                                    {DESTRUCTION_HOLES.map((index) => {
                                        const item = gameDetails.TeeBoxes.ScoreCard[index];
                                        const holeNo = item?.HoleNoValue;
                                        const checked = preferredHoleChecks[holeNo];
                                        const disableUnchecked = !checked && preferredHoleCount >= MAX_PREFERRED_HOLES;
                                        return (
                                            <label key={holeNo} className="kt-checkbox kt-checkbox--bold kt-checkbox--success d-block mb-2">
                                                <input
                                                    type="checkbox"
                                                    checked={checked}
                                                    disabled={disableUnchecked || preferredHolesSaving}
                                                    onChange={() => togglePreferredHole(holeNo)}
                                                />
                                                Hole {holeNo} <span></span>
                                            </label>
                                        );
                                    })}
                                </>
                            )}
                        </div>
                        <div className="modal-footer d-flex align-items-center justify-content-between">
                            <div className="d-flex align-item-center text-center font-weight-bold">
                                <span className={`border badge ${(preferredHoleCount == MAX_PREFERRED_HOLES) ? 'badge-success text-white' : 'badge-light'}`} style={{ fontWeight: 400 }}>
                                                {preferredHoleCount} / {MAX_PREFERRED_HOLES} 
                                </span>
                                <span>Selected</span>
                            </div>
                            <small className="text-muted">{isPreferredHolesDirty ? 'Unsaved changes' : 'All changes saved'}</small>
                            <div>
                                <button type="button" className="btn btn-success btn-sm mr-2" onClick={handleSavePreferredHoles} disabled={preferredHolesSaving || !isPreferredHolesDirty}>
                                    {preferredHolesSaving ? 'Saving...' : 'Save'}
                                </button>
                                <button type="button" className="btn btn-secondary btn-sm" onClick={handleClosePreferredHolesModal} disabled={preferredHolesSaving}>
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        )}
    </>);
}

export default DevilsDoubles666;